import { useLayoutEffect, useState } from 'react';
import { useActiveAccount } from 'thirdweb/react';

import { useEthers } from '@usedapp/core';

import { TEST_ACCOUNT } from '@context/constants';
import { STORAGE_KEY } from '@context/store';
import { hasThirdwebClient } from '@context/thirdweb';

/** Read in-app-wallet mode without react-tracked (safe outside <Provider>). */
function readIsWallet(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (
      new URLSearchParams(window.location.search).get('origin') === 'wallet'
    ) {
      return true;
    }
    const stored = JSON.parse(
      window.sessionStorage.getItem(STORAGE_KEY) || '{}',
    );
    return stored?.isWallet === true;
  } catch {
    return false;
  }
}

/**
 * Single source of truth for "is the user connected?" across the app.
 *
 * When thirdweb powers the connect button (hasThirdwebClient && !isWallet),
 * only thirdweb's session counts — stale @usedapp/core auto-reconnects must
 * not drive wallet-specific UI while the header still shows "Connect".
 *
 * In the Bitcoin.com in-app webview (isWallet) or when thirdweb is not
 * configured, falls back to @usedapp/core as before.
 */
export function useAppWallet() {
  const [isWallet, setIsWallet] = useState(false);
  const {
    account: ethersAccount,
    active,
    library,
    chainId,
    switchNetwork,
  } = useEthers();
  const thirdwebAccount = useActiveAccount();

  useLayoutEffect(() => {
    setIsWallet(readIsWallet());
  }, []);

  const useLegacyWallet = isWallet || !hasThirdwebClient;

  const account =
    TEST_ACCOUNT ||
    (useLegacyWallet ? ethersAccount : thirdwebAccount?.address);

  const isConnected = !!(
    TEST_ACCOUNT ||
    (useLegacyWallet ? active && ethersAccount : thirdwebAccount?.address)
  );

  return {
    account,
    isConnected,
    chainId,
    library,
    switchNetwork,
    useLegacyWallet,
  };
}
